import AccessTimeIcon from '@mui/icons-material/AccessTime'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import {
  AvatarGroup,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  CardMedia,
  CircularProgress,
  Collapse,
  Divider,
  Grid2 as Grid,
  Stack,
  Typography,
} from '@mui/material'
import { useAtomValue } from 'jotai'
import React from 'react'
import { memo } from 'react'
import { shallowEqual } from 'react-redux'
import { useNavigate } from 'react-router'

import ImageGallery from './ImageGallery'
import InfoChips from './InfoChips'
import InfoSection from './InfoSection'
import MenuAction from './MenuAction'
import ShareButton from './ShareButton'
import { getImage } from '../../data/Images'
import { isAuthenticatedAtom } from '../../domains/auth/AtomAuth'
import { STATUS } from '../../share/Constant'
import { AvatarPlus, UserAvatar } from '../../utils/gravatar'
import { formatTimeToAMPM } from '../../utils/TimeUtility'
import ContactCard from '../cards/ContactCard'
import Markdown from '../cards/Markdown'
import UserCard from '../cards/UserCard'
import { useUser, useParticipants } from '../hooks/useUsers'

const DISPLAY_MODES = {
  DETAILED: 'detailed',
  CARD: 'card',
  SMALL: 'small',
}

// Custom Divider
const CDivider = ({ sx = {} }) => {
  return <Divider sx={{ mt: 4, mb: 2, maxWidth: '900px', mx: 'auto', width: '100%', ...sx }} />
}

const CHeader = ({ title }) => {
  return (
    <Typography variant="h6" color="text.secondary">
      {title}
    </Typography>
  )
}

/**
 * A versatile component for displaying event information (Trip/Lesson)
 * @param {Object} props
 * @param {Object} props.event - The event object containing all details
 * @param {string} props.mode - Display mode: 'detailed', 'card', or 'small'
 * @param {Object} props.actions - Actions for the event
 * @param {boolean} props.actions.viewable - Whether the event can be viewed
 * @param {boolean} props.actions.editable - Whether the event can be edited
 * @param {boolean} props.actions.unregisterable - Whether the event can be unregistered
 * @param {boolean} props.actions.deleteable - Whether the event can be deleted
 * @param {function} props.actions.onEdit - Edit handler
 * @param {function} props.actions.onUnregister - Unregister handler
 * @param {function} props.actions.onDelete - Delete handler
 * @param {function} props.actions.onView - View handler
 */
export const EventDisplay = ({
  user,
  event,
  mode = DISPLAY_MODES.CARD,
  link = '',
  share = {
    enabled: false,
    title: '',
    text: '',
    url: '',
  },
  actions = {
    enabled: true,
    viewable: true,
    editable: false,
    unregisterable: false,
    deleteable: false,
    onRegister: () => {},
    onEdit: () => {},
    onUnregister: () => {},
    onDelete: () => {},
    onView: null,
    exportable: false,
    onExport: () => {},
  },
  sx = {},
}) => {
  const navigate = useNavigate()
  const isAuthenticated = useAtomValue(isAuthenticatedAtom)

  const { showSpotsTaken = true, timeSlot, title, description, details, skiResort, date, price, status, sport, participants = [], maxParticipants, notes = [] } = event
  const availableSpots = maxParticipants ? maxParticipants - participants.length : 0
  const [contactCardsExpanded, setContactCardsExpanded] = React.useState(false)

  const { users: participantUsers, isLoading } = useParticipants(participants)
  const { user: instructorNew } = useUser(event.instructorId)

  const image = event.image || getImage(event.resourceId)
  const isInstructorView = event.participantsDetail === true

  const getNotes = () => {
    const participantNote = []
    participants.forEach((participant) => {
      if (participant.note) {
        participantNote.push({
          userId: participant.id,
          note: participant.note,
        })
      }
    })
    // keep the original notes for backword compatibility
    return [...notes, ...participantNote]
  }

  const participantNotes = getNotes()

  const menuAction = () => {
    if (!actions.enabled) return null
    return <MenuAction {...actions} />
  }

  const onClick = () => {
    if (actions.onView) {
      actions.onView()
    } else {
      navigate(`${link}`)
    }
  }

  const renderRegisterButton = () => {
    if (event.status === STATUS.COMPLETED) {
      return
    }

    if (participants.some((participant) => participant.id === user?.resourceId)) {
      return (
        <Button variant="contained" color="primary" size="small" onClick={() => actions.onUnregister()}>
          Unregister
        </Button>
      )
    } else if (event.status === STATUS.AVAILABLE) {
      return (
        <Button variant="contained" color="primary" size="small" onClick={() => actions.onRegister()}>
          Register
        </Button>
      )
    }
  }

  const renderParticipants = (containerSx = {}) => {
    if (!participants?.length || !isAuthenticated) return null

    // if I'm not the participant, I can't see the participants
    if (!participants.some((participant) => participant.id === user?.resourceId) && !isInstructorView) {
      return null
    }

    return (
      <Box sx={{ ...containerSx }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: isInstructorView ? 1 : 0 }}>
          {/* Participants avatar cards */}
          <Typography key="participants" variant="body2" color="text.secondary">
            {isInstructorView && participantUsers.length > 0 ? (
              <Button
                size="medium"
                color="primary"
                onClick={(e) => {
                  e.stopPropagation()
                  setContactCardsExpanded(!contactCardsExpanded)
                }}
              >
                {contactCardsExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                Participants:
              </Button>
            ) : (
              'Participants:'
            )}
          </Typography>
          {isLoading ? (
            <CircularProgress size={24} />
          ) : (
            <>
              <AvatarGroup max={6} sx={{ '& .MuiAvatar-root': { width: 32, height: 32 } }}>
                {participantUsers.map((participant, index) => (
                  <UserAvatar key={participant.resourceId + index} user={participant} size="small" />
                ))}
              </AvatarGroup>
            </>
          )}
        </Box>

        {/* Contact cards with details */}
        {isInstructorView && participantUsers.length > 0 && (
          <Collapse in={contactCardsExpanded} timeout="auto" unmountOnExit onClick={(e) => e.stopPropagation()}>
            <Divider sx={{ my: 2 }} />
            <Typography key="participants-title" variant="subtitle2" color="text.secondary" sx={{ mb: 2 }}>
              Participant Contact Information
            </Typography>
            <Grid container spacing={2}>
              {participantUsers.map((participant, index) => (
                // Make sure this is a unique key
                <Grid key={participant.resourceId + index}>
                  <ContactCard user={participant} size="small" emergencyContact={true} disableNavigation={true} />
                </Grid>
              ))}
            </Grid>
          </Collapse>
        )}
      </Box>
    )
  }

  /**
   * Renders a detailed view of an event with full information
   * Includes:
   * - Large header image
   * - Title and description
   * - Event details in InfoSection (location, date, status, etc)
   * - Markdown formatted details
   * - Price and registration button
   */
  const renderDetailedView = () => (
    <Card sx={sx}>
      {/* Header image with menu overlay */}
      <Box sx={{ position: 'relative' }}>
        <CardMedia component="img" height="300" image={image} alt={title} />
        <Grid container direction="row" justifyContent="flex-end" sx={{ position: 'absolute', top: 5, right: 1, bgcolor: 'transparent' }}>
          {share.enabled && <ShareButton title={share.title} text={share.text} url={share.url} size="small" />}
          {menuAction()}
        </Grid>
      </Box>

      {/* Title and description */}
      <Typography variant="h4" gutterBottom sx={{ mt: 4 }}>
        {' '}
        {title}{' '}
      </Typography>
      {description && (
        <Typography variant="body1" color="text.secondary" paragraph sx={{ mt: 2 }}>
          {description}
        </Typography>
      )}
      <InfoChips info={event} />

      <CardContent sx={{ height: '100%' }}>
        {/* Instructor Section */}
        {instructorNew && (
          <Box alignItems="center" justifyContent="center" sx={{ mt: 0 }}>
            <CDivider />
            <UserCard user={instructorNew} bio={false} size="medium" sx={{ border: 'none' }} />
          </Box>
        )}

        {/* Event info section */}
        <CDivider />
        <InfoSection
          info={{
            location: skiResort,
            date,
            timeSlot,
            showSpotsTaken,
            maxParticipants,
            participants,
            availableSpots,
            registrationDeadline: event.registrationDeadline,
            cancellationDeadline: event.cancellationDeadline,
          }}
          sx={{ maxWidth: '600px', mx: 'auto', width: '100%' }}
        />

        {/* Participants Section */}
        {renderParticipants({ maxWidth: '600px', mx: 'auto', width: '100%' })}

        {/* Time Slots Section */}
        {event.times && event.times.length > 0 && (
          <>
            <CDivider />
            <Box sx={{ maxWidth: '600px', mx: 'auto', width: '100%' }}>
              <CHeader title="Session Hours" />
              <Stack alignItems="center" spacing={1} sx={{ mt: 1 }}>
                {event.times.map((timeSlot, index) => (
                  <Box
                    key={index}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      borderRadius: 1,
                    }}
                  >
                    <AccessTimeIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {formatTimeToAMPM(timeSlot.startTime)} - {formatTimeToAMPM(timeSlot.endTime)}
                    </Typography>
                  </Box>
                ))}
              </Stack>
            </Box>
          </>
        )}

        {/* Markdown formatted details */}
        {details && (
          <>
            <CDivider />
            <Box sx={{ mt: 2, maxWidth: '700px', mx: 'auto', width: '100%' }}>
              {/* <ReactMarkdown>{details}</ReactMarkdown> */}
              <Markdown>{details}</Markdown>
            </Box>
          </>
        )}

        {/* 添加学生笔记部分 */}
        {participantNotes.length > 0 && isInstructorView && (
          <>
            <CDivider />
            <Box sx={{ maxWidth: '700px', mx: 'auto', width: '100%' }}>
              <CHeader title="Student Notes" />
              <Stack spacing={2} sx={{ mt: 2 }}>
                {participantNotes.map((noteItem, index) => {
                  const noteUser = participantUsers.find((user) => user.resourceId === noteItem.userId)
                  if (!noteUser) return null
                  return (
                    <Card
                      key={index}
                      sx={{
                        p: 2,
                        boxShadow: 'none',
                        bgcolor: 'background.paper',
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 2,
                      }}
                    >
                      <Box sx={{ display: 'flex', gap: 2 }}>
                        <Box>
                          <UserAvatar user={noteUser} size="medium" />
                        </Box>
                        <Box sx={{ flex: 1 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                            <Typography variant="subtitle1" fontWeight="500">
                              {noteUser?.firstName} {noteUser?.lastName}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {noteItem.date
                                ? new Date(noteItem.date).toLocaleDateString('en-US', {
                                    day: '2-digit',
                                    month: 'short',
                                    year: 'numeric',
                                  })
                                : ''}
                            </Typography>
                          </Box>
                          <Typography
                            variant="body1"
                            color="text.secondary"
                            sx={{
                              whiteSpace: 'pre-line',
                              textAlign: 'left',
                            }}
                          >
                            {noteItem.note}
                          </Typography>
                        </Box>
                      </Box>
                    </Card>
                  )
                })}
              </Stack>
            </Box>
          </>
        )}

        {/* Session Media Gallery */}
        {event.pictures && event.pictures.length > 0 && (
          <>
            <CDivider />
            <ImageGallery
              images={event.pictures.map((picture) => picture.url || picture.r2Url || picture)}
              title="Session Media Gallery"
              collapsible={true}
              defaultExpanded={true}
              autoplay={true}
              autoplayDelay={3000}
              effect="coverflow"
              sx={{ maxWidth: '900px', mx: 'auto', width: '100%' }}
            />
          </>
        )}
      </CardContent>

      {/* Price and registration */}
      <CDivider />
      <CardActions sx={{ justifyContent: 'flex-end', pt: 2, pl: 2, pr: 0, height: '100%' }}>
        {price && (
          <Typography variant="h6" color="primary" sx={{ mr: 'auto' }}>
            ${price}
          </Typography>
        )}

        <Box sx={{ display: 'flex', gap: 1 }}>{renderRegisterButton()}</Box>
      </CardActions>
    </Card>
  )

  const renderCardView = () => (
    <Card
      sx={{
        ...sx,
        transition: 'all 0.2s ease',
        '&:hover': {
          boxShadow: (theme) => theme.shadows[4],
        },
      }}
    >
      {/* 显示事件图片和标题，右上角显示菜单 */}
      <Box sx={{ position: 'relative' }}>
        {/* 事件图片，如果没有提供则使用默认图片 */}
        <CardMedia component="img" height="200" image={image} alt={title} />
        {/* 右上角菜单，阻止点击事件冒泡以避免触发卡片点击 */}
        <Box sx={{ position: 'absolute', top: 5, right: 0, bgcolor: 'transparent' }} onClick={(e) => e.stopPropagation()}>
          {menuAction()}
        </Box>
      </Box>

      <Box
        onClick={onClick}
        sx={{
          cursor: 'pointer',
        }}
      >
        {/* Event Title */}
        <Typography variant="h5" gutterBottom sx={{ mt: 2 }}>
          {title}
        </Typography>

        {instructorNew && (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mt: 1, mb: 1 }}>
            <AvatarPlus user={instructorNew} size="small" />
          </Box>
        )}

        {description && (
          <Typography variant="body1" color="text.secondary" paragraph sx={{ mt: 2 }}>
            {description}
          </Typography>
        )}
        <InfoChips info={{ location: skiResort, sport }} size="small" />

        <CardContent sx={{ height: '100%' }}>
          <Divider sx={{ mt: 2, mb: 2 }} />

          <InfoSection
            info={{
              date,
              timeSlot,
              status,
              price,
              maxParticipants,
              participants,
              availableSpots,
              showSpotsTaken: false,
            }}
            columns={1}
            hideLabels={true}
            sx={{ maxWidth: '300px', mx: 'auto', width: '100%' }}
          />
        </CardContent>
        {renderParticipants()}

        <Divider sx={{ mt: 2 }} />
      </Box>

      <CardActions sx={{ justifyContent: 'flex-end', pt: 2, pl: 2, pr: 0, height: '100%' }}>
        {price && (
          <Typography variant="h6" color="primary" onClick={onClick} sx={{ marginRight: 'auto', cursor: 'pointer' }}>
            ${price}
          </Typography>
        )}
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="contained" color="primary" size="small" onClick={onClick}>
            Details
          </Button>
          {renderRegisterButton()}
        </Box>
      </CardActions>
    </Card>
  )

  const renderSmallView = () => (
    <Card
      onClick={onClick}
      sx={{
        ...sx,
        maxWidth: '1000px',
        mx: 'auto',
        width: '100%',
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        '&:hover': {
          // transform: 'translateY(-2px)',
          boxShadow: (theme) => theme.shadows[4],
        },
      }}
    >
      <CardHeader action={<Box onClick={(e) => e.stopPropagation()}>{menuAction()}</Box>} title={title} />
      <CardContent sx={{ mt: 0 }}>
        {/* Instructor Section */}
        {instructorNew && (
          <>
            <Grid container alignItems="center" direction="row" justifyContent="center" sx={{ my: 0 }}>
              <UserCard user={instructorNew} bio={false} size="small" sx={{ '&:hover': {}, border: 'none' }} />
            </Grid>
            <Divider sx={{ mt: 0, mb: 1 }} />
          </>
        )}
        <InfoSection
          info={{
            location: skiResort,
            date,
            timeSlot,
            status,
            sport,
            showSpotsTaken,
            maxParticipants: event.maxParticipants,
            participants,
          }}
          columns={3}
          hideLabels={true}
          sx={{ mt: 1.5 }}
        />

        {participants?.length > 0 && (
          <>
            <Divider sx={{ my: 1 }} />
            {/* Participants Section */}
            {renderParticipants()}
          </>
        )}
      </CardContent>
    </Card>
  )

  switch (mode) {
    case DISPLAY_MODES.DETAILED:
      return renderDetailedView()
    case DISPLAY_MODES.SMALL:
      return renderSmallView()
    case DISPLAY_MODES.CARD:
    default:
      return renderCardView()
  }
}

export default memo(EventDisplay, shallowEqual)
